import { useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useNotification } from '@/_shared/hooks';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentMembersCall } from '@/departments/state/api';
import { getDelegatesCall, postCreateDelegateEmployerCall } from '@/organization/state/api';

type Props = {
  selectedEmployees?: ReturnType<typeof getDepartmentMembersCall.hook>['allEmployees'];
  departmentName: string;
  goBack: () => void;
  onConfirm: () => void;
};

export const ConfirmDepartmentEmployees = ({
  selectedEmployees,
  departmentName,
  goBack,
  onConfirm,
}: Props) => {
  const t = useTranslations('settings.tabs.delegates.addDelegate.dialog.delegateEmployees.confirm');
  const tShared = useTranslations();

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();

  const gapM = convertSizeToNumber(`${useThemeValue('sizes', 'm')}`);
  const { execute: executePostCreateDelegateEmployer, loading: loadingPostCreateDelegateEmployer } =
    postCreateDelegateEmployerCall.hook();
  const { execute: executeGetDelegates } = getDelegatesCall.hook();
  const { notify } = useNotification();

  const onSubmit = useCallback(() => {
    if (!selectedEmployees?.length) {
      return;
    }
    executePostCreateDelegateEmployer({
      employeeIds: selectedEmployees.map(emp => emp.id),
      successSaga: () => {
        notify(tShared('success.genericApiSuccess'), 'success');
        executeGetDelegates({
          start: 0,
          length: 1000,
        });
        onConfirm();
      },
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executePostCreateDelegateEmployer, onConfirm, selectedEmployees]);

  return (
    <Box mt='xxs'>
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='p2/light' textAlign='center'>
          {t('description')}
        </Typography>
      </Box>
      <Box mt='m'>
        {selectedEmployees?.map(employer => (
          <Card key={employer.id} mb='xs' padding='xs' bg='grey/dark/background' borderRadius='s'>
            <Box display='flex' alignItems='center' flexDirection='row' mb='s'>
              <Svg name='user' size='xs' />
              <Typography variant='p1' textAlign='center' ml='xxs'>
                {employer.name}
              </Typography>
            </Box>
            <Box
              display='flex'
              alignItems='flex-start'
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              flex={1}
              style={{ gap: gapM }}
            >
              <Box>
                <Typography variant='p2/light'>{t('department')}</Typography>
                <Typography variant='p1/light' fontWeight={400}>
                  {departmentName}
                </Typography>
              </Box>
              <Box flex={1}>
                <Typography variant='p2/light'>{t('role')}</Typography>
                <Typography variant='p1/light' fontWeight={400}>
                  {employer.role || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='p2/light'>{t('email')}</Typography>
                <Typography variant='p1/light' fontWeight={400}>
                  {employer.email}
                </Typography>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>
      <Box mt='m'>
        <AlertBadge type='transparent' label={t('infoBadge')} iconName='info' />
      </Box>
      <Box
        mt='m'
        justifyContent='center'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        width={genericResponsiveValue({
          breakPoints: { desktop: 'auto' },
          defaultValue: '100%',
        })}
        alignItems={genericResponsiveValue({
          breakPoints: { desktop: 'auto' },
          defaultValue: 'center',
        })}
        display='flex'
        style={{ gap: isDesktop ? gapM : 0 }}
      >
        <Button onClick={goBack} textAlign='left' variant='link/primary' mb='s' mt='none'>
          <Typography ml='xxxs' variant='p2'>
            {t('cta.changeSelection')}
          </Typography>
        </Button>
        <Button
          onClick={onSubmit}
          textAlign='left'
          variant='primary'
          mb='s'
          mt='none'
          disabled={loadingPostCreateDelegateEmployer}
          loading={loadingPostCreateDelegateEmployer}
        >
          {t('cta.confirm')}
        </Button>
      </Box>
    </Box>
  );
};
